import Phaser from 'phaser';
import { FONT_FAMILY } from '../config/constants';
import { getGameplayColorHex } from '../config/colors';
import { crispText } from './textUtils';
import { buildReachMap, type BoardSnapshot } from '../systems/BotStrategy';
import { BoardSystem, cellKey } from '../systems/BoardSystem';

interface ReachEntry {
  colorIndex: number;
  count: number;
}

/**
 * HUD overlay showing how many cubes of each color are currently
 * reachable (edge-exposed) by attackers on the conveyor.
 * Positioned along the right edge of the board area.
 */
export class ReachHUD {
  private scene: Phaser.Scene;
  private board: BoardSystem;
  private container: Phaser.GameObjects.Container;
  private entries: { dot: Phaser.GameObjects.Graphics; label: Phaser.GameObjects.Text }[] = [];
  private dirty = true;

  constructor(
    scene: Phaser.Scene,
    board: BoardSystem,
    x: number,
    y: number,
  ) {
    this.scene = scene;
    this.board = board;
    this.container = scene.add.container(x, y).setDepth(90);
  }

  markDirty(): void {
    this.dirty = true;
  }

  update(): void {
    if (!this.dirty) return;
    this.dirty = false;

    const snapshot = this.buildSnapshot();
    const reachMap = buildReachMap(snapshot);

    // Convert to sorted array (descending by count)
    const sorted: ReachEntry[] = [];
    for (const [colorIndex, count] of reachMap) {
      if (count > 0) {
        sorted.push({ colorIndex, count });
      }
    }
    sorted.sort((a, b) => b.count - a.count);

    // Clear old entries
    for (const e of this.entries) {
      e.dot.destroy();
      e.label.destroy();
    }
    this.entries = [];

    // Draw background panel
    this.container.removeAll(true);

    if (sorted.length === 0) return;

    const rowH = 26;
    const panelH = sorted.length * rowH + 12;
    const panelW = 80;

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x000000, 0.4);
    bg.fillRoundedRect(0, 0, panelW, panelH, 6);
    this.container.add(bg);

    for (let i = 0; i < sorted.length; i++) {
      const { colorIndex, count } = sorted[i];
      const ry = 8 + i * rowH;

      // Colored dot
      const dot = this.scene.add.graphics();
      const color = getGameplayColorHex(colorIndex);
      dot.fillStyle(color, 1);
      dot.fillCircle(14, ry + 9, 7);
      this.container.add(dot);

      // Count label
      const label = crispText(
        this.scene.add.text(30, ry, `${count}`, {
          fontFamily: FONT_FAMILY,
          fontSize: '18px',
          color: '#ffffff',
        }),
        false,
      );
      this.container.add(label);

      this.entries.push({ dot, label });
    }
  }

  private buildSnapshot(): BoardSnapshot {
    const rows = this.board.rows;
    const cols = this.board.cols;

    const grid: number[][][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[][] = [];
      for (let c = 0; c < cols; c++) {
        row.push([...this.board.getStack(r, c)]);
      }
      grid.push(row);
    }

    const snakeCells = new Set<number>();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.board.isSnakeCell(r, c)) {
          snakeCells.add(cellKey(r, c));
        }
      }
    }

    const megaBlocks = new Map<number, { id: number; colorIndex: number; hp: number; row: number; col: number; width: number; height: number }>();
    const megaCells = new Set<number>();
    const activeMega = this.board.getMegaBlocks();
    if (activeMega) {
      for (const [id, mb] of activeMega) {
        megaBlocks.set(id, {
          id,
          colorIndex: mb.colorIndex,
          hp: mb.hp,
          row: mb.row,
          col: mb.col,
          width: mb.width,
          height: mb.height,
        });
        for (let r = mb.row; r < mb.row + mb.height; r++) {
          for (let c = mb.col; c < mb.col + mb.width; c++) {
            megaCells.add(cellKey(r, c));
          }
        }
      }
    }

    const doorCells = new Set<number>();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.board.isDoorCell(r, c)) {
          doorCells.add(cellKey(r, c));
        }
      }
    }

    const barricadeCells = new Set<number>();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.board.isBarricadeCell(r, c)) {
          barricadeCells.add(cellKey(r, c));
        }
      }
    }

    return {
      grid,
      rows,
      cols,
      snakeCells,
      megaBlocks,
      megaCells,
      doorCells,
      barricadeCells,
    };
  }

  destroy(): void {
    this.container.destroy(true);
  }
}
